import React, { useCallback } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { GeneralEventScheduleDataDay, GeneralEventScheduleDataItem, ScheduleDisplayTypes } from '../schedule.types';
import { BasicScheduleDay } from './basic/BasicScheduleDay';
import { ModernScheduleDay } from './modern/ModernScheduleDay';
import { DefaultSkeleton } from './DefaultSkeleton';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { TwoColumnSchedule } from './two-column/TwoColumnSchedule';

export type ScheduleDayProps = {
  data: GeneralEventScheduleDataDay;
};

export type ScheduleItemProps = {
  data: GeneralEventScheduleDataItem;
};

type ScheduleDisplayProps = {
  type: ScheduleDisplayTypes;
  data: GeneralEventScheduleDataDay[] | null;
};

export const ScheduleDisplay = (props: ScheduleDisplayProps) => {
  const { type, data: days } = props;

  const renderScheduleDay = useCallback(
    (day: GeneralEventScheduleDataDay) => {
      const key = day.dateISO8601TZ;
      switch (type) {
        case 'basic':
          return (
            <BasicScheduleDay
              key={key}
              data={day}
            />
          );
        case 'modern':
          return (
            <ModernScheduleDay
              key={key}
              data={day}
            />
          );
        case 'two-column':
          return (
            <TwoColumnSchedule
              key={key}
              data={day}
            />
          );
        default:
          return null;
      }
    },
    [type],
  );

  const renderContent = useCallback(() => {
    if (!days || days.length === 0) {
      return (
        <Flex
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          padding="2rem"
        >
          <TextV2
            typographyVariant="body1"
            color="textSecondary"
          >
            No events scheduled.
          </TextV2>
        </Flex>
      );
    }

    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        {days.map(day => renderScheduleDay(day))}
      </Flex>
    );
  }, [days, renderScheduleDay]);

  return (
    <>
      <SkeletonGroup
        placeholder={<DefaultSkeleton />}
        isReady={!!days}
        width="100%"
      >
        {renderContent()}
      </SkeletonGroup>
    </>
  );
};
